import { type Common } from '../common.js';
import { type ChatSessionWithMessages } from '../models/assistant/index.js';
import { SortOrder } from '../models/common.js';
import {
  type FlowBuilderAssistantFindSessionsResponse,
  type FlowBuilderAssistantSubmitMessageFeedbackRequest,
  flowBuilderAssistantSubmitMessageFeedbackRequestSchema,
} from '../models/flow/assistant.js';

export class API {
  common: Common;
  baseEndpoint = '/flow/assistant';

  constructor(common: Common) {
    this.common = common;
  }

  async addMessageToConversation(args: {
    sessionId: string;
    message: string;
    organizationId: string;
    flowDefinitionId?: string;
    flowRunId?: string;
    flowBuilderNodesAndEdges?: Record<string, unknown>;
    selectedNodeIds?: string[];
  }): Promise<ReadableStream<string>> {
    const url = this.common.constructPath(`${this.baseEndpoint}/conversation`);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'text/event-stream',
        Connection: 'keep-alive',
        'Content-Type': 'application/json',
      },
      cache: 'no-store',
      body: JSON.stringify(args),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}.`);
    }

    if (!response?.body) {
      throw new Error('No response body');
    }

    return response.body.pipeThrough(new TextDecoderStream());
  }

  async submitMessageFeedback(
    args: FlowBuilderAssistantSubmitMessageFeedbackRequest
  ): Promise<void> {
    const { messageId, ...body } =
      flowBuilderAssistantSubmitMessageFeedbackRequestSchema.parse(args);
    const url = this.common.constructPath(
      `${this.baseEndpoint}/conversation/messages/${messageId}/feedback`
    );
    await this.common.postJSON(url, body);
  }

  findSessions(args: {
    organizationId: string;
    flowDefinitionId: string;
  }): Promise<FlowBuilderAssistantFindSessionsResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/sessions`, {
      ...args,
      orderBy: 'created',
      orderByDirection: SortOrder.Descending,
    });
    return this.common.getJSON(url);
  }

  getMessagesFromSession(sessionId: string): Promise<ChatSessionWithMessages> {
    const url = this.common.constructPath(`${this.baseEndpoint}/sessions/${sessionId}/messages`);
    return this.common.getJSON(url);
  }
}
